/**
 * Records cache: read, merge, dedup, sort, and evict the paginated health
 * records list for a single customer.
 *
 * Health records originate from multiple source tables, so a record's `id` is
 * not globally unique. The Record_Key (`source_table + "::" + id`) is the true
 * identity used for deduplication and merge; the `::` delimiter prevents
 * accidental collisions (e.g. table `h_no` + id `te1` vs table `h_note` + id
 * `1`).
 *
 * Stored records are kept sorted by `event_timestamp` (descending, newest
 * first — matching the data-model example) and capped at `RECORDS_LIMIT`
 * entries by evicting the oldest `event_timestamp` first.
 *
 * Satisfies:
 * - Requirement 5.1: retain non-superseded existing historical records.
 * - Requirement 5.2: retain all records when count <= Records_Limit.
 * - Requirement 5.3: evict oldest Event_Timestamp first beyond Records_Limit.
 * - Requirement 5.4: store records sorted by Event_Timestamp.
 * - Requirement 7.1: save fetched records keyed by customer id.
 * - Requirement 7.2: merge fetched values into an existing record on key match.
 * - Requirement 7.3: identify records for dedup/merge using the Record_Key.
 */

import { readJson } from './storage';
import { HEALTH_RECORDS_KEY } from './keys';

/**
 * A raw health record as returned by the server. Only the identity and sort
 * fields are typed; all other server fields pass through verbatim.
 */
export interface RawHealthRecord {
  id: string | number;
  source_table: string;
  event_timestamp: string;
  [k: string]: unknown; // other server fields passed through verbatim
}

/** Maximum number of records retained in the cache for one customer. */
export const RECORDS_LIMIT = 1000;

/**
 * Derive the Record_Key for a record: `source_table` concatenated with `id`
 * via a `::` delimiter (Req 7.3).
 */
export function recordKey(r: RawHealthRecord): string {
  return `${r.source_table}::${r.id}`;
}

/**
 * Read the stored records list for a customer.
 *
 * @returns the cached records array, or `[]` on a cache miss / parse failure.
 */
export function readRecords(customerId: string): RawHealthRecord[] {
  const stored = readJson<RawHealthRecord[]>(HEALTH_RECORDS_KEY(customerId));
  return Array.isArray(stored) ? stored : [];
}

/**
 * Compare two records for descending `event_timestamp` order (newest first).
 */
function byEventTimestampDesc(a: RawHealthRecord, b: RawHealthRecord): number {
  // Lexicographic comparison works for ISO-8601 timestamps; fall back to it
  // for any other string format too.
  if (a.event_timestamp < b.event_timestamp) return 1;
  if (a.event_timestamp > b.event_timestamp) return -1;
  return 0;
}

/**
 * Merge a fetched page of records into the existing cached list.
 *
 * Behavior:
 * - Dedup/merge by Record_Key. On a key collision the fetched values supersede
 *   the existing ones (existing fields are merged under the fetched record so
 *   fields only present on the existing record are preserved) (Req 7.2).
 * - Existing records whose Record_Key is absent from the fetched list are
 *   retained (Req 5.1).
 * - Records with equal `id` but different `source_table` remain distinct.
 * - The result is sorted by `event_timestamp`, newest first (Req 5.4).
 * - When the union exceeds `RECORDS_LIMIT`, the oldest `event_timestamp`
 *   records are evicted until exactly `RECORDS_LIMIT` remain (Req 5.3).
 *
 * @returns the new merged, sorted, and bounded list.
 */
export function mergeRecords(
  existing: RawHealthRecord[],
  fetched: RawHealthRecord[],
): RawHealthRecord[] {
  const merged = new Map<string, RawHealthRecord>();

  // Seed with existing records, EXCLUDING optimistic local records (rec- prefix)
  // when we have fetched data (server data supersedes local optimistic entries).
  for (const record of existing) {
    const isLocalOptimistic = String(record.id).startsWith('rec-');
    if (fetched.length > 0 && isLocalOptimistic) {
      // Skip local optimistic records — they'll be replaced by real server data
      continue;
    }
    merged.set(recordKey(record), record);
  }

  // Apply fetched records: fetched values supersede existing on key collision,
  // while preserving any existing-only fields (Req 7.2).
  for (const record of fetched) {
    const key = recordKey(record);
    const prior = merged.get(key);
    merged.set(key, prior ? { ...prior, ...record } : record);
  }

  // Sort by event_timestamp, newest first (Req 5.4).
  const sorted = Array.from(merged.values()).sort(byEventTimestampDesc);

  // Evict the oldest event_timestamp records beyond the limit (Req 5.3). Since
  // the list is sorted newest-first, the oldest entries are at the tail.
  if (sorted.length > RECORDS_LIMIT) {
    return sorted.slice(0, RECORDS_LIMIT);
  }

  return sorted;
}
